import contentMapApi from "@/apis/content.map.api";
import subjectApi from "@apis/subject.api.ts";
import { isDefined, isEmpty } from "@/utils/var.check";
import { reactive } from "vue";
import { sortSubjectsDeprecatedLast } from "@utils/subject.utils";

/**
 * This store will mainly be used by resource tagging selection components
 */
export default {
  state: reactive({
    subjectsById: {}, //subject with level and content map
    contentMapByUuid: {}, //contentmap with subject and level
    hasViewModeContentMapOnly: false,
  }),
  hasViewModeContentMapOnly() {
    return this.state.hasViewModeContentMapOnly;
  },
  setHasViewModeContentMapOnly(payload) {
    this.state.hasViewModeContentMapOnly = payload;
  },
  getSubject(id) {
    return this.state.subjectsById[id];
  },
  getContentMap(uuid) {
    return this.state.contentMapByUuid[uuid];
  },
  getSubjects() {
    return Object.values(this.state.subjectsById);
  },
  getSubjectsByType(type) {
    return sortSubjectsDeprecatedLast(
      Object.values(this.state.subjectsById).filter((subject) => subject.groupingCategory === type),
    );
  },
  getSubjectLevelsByType(id, type) {
    if (id.split(",").length > 1) {
      return [];
    }

    const subject = this.getSubject(id);

    if (!subject || !subject.levelAndStreams) {
      return [];
    }

    return Object.values(subject.levelAndStreams)
      .map((levelAndStream) => {
        return levelAndStream;
      })
      .reduce(function (filtered, levelAndStream) {
        if (levelAndStream.groupingCategory === type) {
          filtered.push(levelAndStream);
        }
        return filtered;
      }, []);
  },
  addSubject(payload) {
    this.state.subjectsById[payload.subjectId] = payload;
  },
  addContentMap(payload) {
    Object.assign(this.state.contentMapByUuid, payload);
  },
  fetchCategorizedSubject() {
    return new Promise((resolve, reject) => {
      subjectApi.getCategorizedSubjects().then(
        //OK
        (response) => {
          if (response.code !== "ok") {
            reject(response.errorMap);
            return;
          }
          this.state.subjectsById = {};

          if (!isEmpty(response.data)) {
            response.data.forEach((subject) => {
              subject.isFullyTagged = false;
              this.addSubject(subject);
            });
          }

          resolve();
        },
        (error) => {
          reject(error);
        },
      );
    });
  },
  fetchLevelAndStreamContentMap(subjectIds) {
    return new Promise((resolve, reject) => {
      contentMapApi.fetchLevelAndStreamContentMap(subjectIds).then(
        //OK
        (response) => {
          if (response.code !== "ok") {
            reject(response.errorMap);
            return;
          }
          // process data
          if (!isEmpty(response.data)) {
            this.processLevelAndStreamData(response.data).then((response) => {
              if (response) {
                resolve();
              }
            });
          } else {
            resolve();
          }
        },
        (error) => {
          reject(error);
        },
      );
    });
  },
  processSubjectLevelAndStreamData: function (data, hasViewModeContentMapOnly = false) {
    // hasViewModeContentMapOnly - used in ResourceTaggingsEdit to determine whether need to fetch all active content maps when enter edit mode
    this.state.subjectsById = {};
    return new Promise((resolve) => {
      const contentmapMap = {};
      data.forEach((subject) => {
        subject.levelAndStreams.forEach((levelAndStream) => {
          levelAndStream.contentMaps.forEach((contentMap) => {
            contentMap.isFullyTagged = false; // isFullyTagged value will be used to filter the drop down later

            if (isDefined(contentmapMap[contentMap.uuid])) {
              if (isDefined(contentmapMap[contentMap.uuid].subjects[subject.subjectId])) {
                contentmapMap[contentMap.uuid].subjects[subject.subjectId].levelAndStreams[
                  levelAndStream.id
                ] = {
                  name: levelAndStream.displayName,
                };
              } else {
                contentmapMap[contentMap.uuid].subjects[subject.subjectId] = {
                  name: subject.subjectName,
                  levelAndStreams: {
                    [levelAndStream.id]: {
                      name: levelAndStream.displayName,
                    },
                  },
                };
              }
            } else {
              contentmapMap[contentMap.uuid] = {
                subjects: {
                  [subject.subjectId]: {
                    name: subject.subjectName,
                    levelAndStreams: {
                      [levelAndStream.id]: {
                        name: levelAndStream.displayName,
                      },
                    },
                  },
                },
              };
            }
          });

          levelAndStream.isFullyTagged = false; // isFullyTagged value will be used to filter the drop down later
        });

        subject.isFullyTagged = false; // isFullyTagged value will be used to filter the drop down later
        this.addSubject(subject);
      });
      this.addContentMap(contentmapMap);
      this.setHasViewModeContentMapOnly(hasViewModeContentMapOnly);
      resolve(true);
    });
  },
  processLevelAndStreamData: function (data, hasViewModeContentMapOnly = false) {
    // hasViewModeContentMapOnly - used in ResourceTaggingsEdit to determine whether need to fetch all active content maps when enter edit mode
    return new Promise((resolve) => {
      const contentmapMap = {};
      data.forEach((subject) => {
        subject.levelAndStreams.forEach((levelAndStream) => {
          levelAndStream.contentMaps.forEach((contentMap) => {
            contentMap.isFullyTagged = false; // isFullyTagged value will be used to filter the drop down later

            if (isDefined(contentmapMap[contentMap.uuid])) {
              if (isDefined(contentmapMap[contentMap.uuid].subjects[subject.subjectId])) {
                contentmapMap[contentMap.uuid].subjects[subject.subjectId].levelAndStreams[
                  levelAndStream.id
                ] = {
                  name: levelAndStream.displayName,
                };
              } else {
                contentmapMap[contentMap.uuid].subjects[subject.subjectId] = {
                  name: subject.subjectName,
                  levelAndStreams: {
                    [levelAndStream.id]: {
                      name: levelAndStream.displayName,
                    },
                  },
                };
              }
            } else {
              contentmapMap[contentMap.uuid] = {
                subjects: {
                  [subject.subjectId]: {
                    name: subject.subjectName,
                    levelAndStreams: {
                      [levelAndStream.id]: {
                        name: levelAndStream.displayName,
                      },
                    },
                  },
                },
              };
            }
          });

          levelAndStream.isFullyTagged = false;
        });

        subject.isFullyTagged = false;

        if (this.state.subjectsById[subject.subjectId]) {
          this.state.subjectsById[subject.subjectId] = {
            ...this.state.subjectsById[subject.subjectId],
            ...subject,
          };
        } else {
          this.addSubject(subject);
        }
      });
      this.addContentMap(contentmapMap);
      this.setHasViewModeContentMapOnly(hasViewModeContentMapOnly);
      resolve(true);
    });
  },
};
